<template>
  <div v-loading="loading" class="container" element-loading-background="transparent"></div>
</template>

<script setup>
import { getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWinAppVersion, isInMacWebview, isInWinWebview, pushDebugInfo, setLocalStorage, setSessionStorage, setToken } from '@/libs/utils'
import { decodeTokenData, resetTokenByCode } from '@/services/login'
import { redirectToAccountCenter, redirectToLogin } from '@/services/base'
import { getLoggedAccounts } from '@/services/webview'
import gtagTools from '@/libs/gtag-tools'
import { map } from 'lodash'
import { getLanguage } from '@/services/store'
import { useAppStore } from '@/stores/modules/appStore'
import { useUserStore } from '@/stores/modules/userStore'

const { locale } = getCurrentInstance().proxy
const appStore = useAppStore()
const userStore = useUserStore()

const $route = useRoute()
const $router = useRouter()
const loading = ref(true)
const action = $route.params.action // 操作类型：如登录：login，用来跳转对应页面

// 设置语言
const language = getLanguage(appStore.isCN, $route.params.lang)
appStore.language = language
locale.value = language

const tokenData = $route.query.tokenData ? decodeTokenData($route.query.tokenData) : '' // base64 后的token数据{token: xxxxx, expired: xxxxx}
const code = $route.query.code || '' // code用于获取token，有code的话需要获取并更新token
const userSync = $route.query.userSync || 0 // 配合code实现自动登录
const token = $route.query.token || ''
const tokenExpired = $route.query.tokenExpired || ''
const schemeName = $route.query.schemeName || ''
if (!schemeName) {
  setLocalStorage('schemeName', '')
  appStore.schemeName = ''
} else {
  setLocalStorage('schemeName', schemeName)
  appStore.schemeName = schemeName
}

// 标记是从入口文件来的(只在本次会话期内)，方便其他页面做判断
setSessionStorage('fromIndex', true)

onMounted(async () => {
  try {
    if (tokenData && tokenData.token && tokenData.expired) {
      setToken(tokenData.token, tokenData.expired)
    } else if (token && tokenExpired) {
      setToken(token, tokenExpired)
    } else if (code && userSync) {
      // 如果有code，则通过code获取token
      await resetTokenByCode(code)
    }
    if (action === 'login') {
      try {
        pushDebugInfo(getWinAppVersion())
        if (isInWinWebview() || isInMacWebview()) {
          let loggedAccounts = await getLoggedAccounts()
          loggedAccounts = JSON.parse(loggedAccounts)
          pushDebugInfo('--------------已登录账号')
          pushDebugInfo(loggedAccounts)
          if (loggedAccounts && loggedAccounts.length) {
            const loggedAccountsUids = map(loggedAccounts, 'Uid')
            userStore.loggedAccounts.push(...loggedAccountsUids)
          }
        }
      } catch (err) {
        pushDebugInfo(err)
      }
      // 登录，中文跳转到微信登录，英文跳转邮箱登录
      // 跳转到登录的话先清空下token
      setToken('', 0)
      redirectToLogin()
    } else if (action === 'loginInfoReport') {
      // 直接进入营销数据信息上报页
      $router.replace({ name: 'loginInfoReport', query: $route.query })
    } else if (action === 'accountCenter') {
      // 个人中心
      redirectToAccountCenter()
    } else if (action === 'planList') {
      // 订阅列表
      const planId = $route.query.planId ? $route.query.planId : 0
      $router.replace({ name: 'iframePlanList', query: { planId } })
    } else if (action === 'companyInvite') {
      // 企业邀请加入
      $router.replace({ name: 'loginJoinConfirm', query: $route.query })
    } else if (action === 'companyManagement') {
      // 企业管理控制台
      $router.replace({ name: 'companyManagement', query: $route.query })
    } else if (action === 'companyCreate') {
      // 创建企业
      $router.replace({ name: 'companyCreate', query: $route.query })
    } else if (action === 'redirect') {
      // 跳转链接
      const url = decodeURIComponent($route.query.url)
      location.href = url
      // Mac端谷歌登录记录谷歌事件
      if (url.includes('accounts.google.com')) {
        gtagTools.jumpThirdParty('google')
      }
    }
    loading.value = false
  } catch (err) {
    redirectToLogin()
    loading.value = false
  }
})
</script>
<style lang="scss" scoped>
.container {
  width: 100%;
  height: 500px;
}
</style>
